<template>
  <div
    class="countdown"
    :class="{
      'countdown--editorial': theme === 'editorial',
      'countdown--unit-labels': unitLabels,
    }"
  >
    <div class="unit">
      <span class="value">{{ days }}</span>
      <span v-if="unitLabels" class="unit-label">{{ dayLabel }}</span>
    </div>
    <div v-if="!unitLabels" class="divider">{{ divider }}</div>
    <div class="unit">
      <span class="value">{{ hours }}</span>
      <span v-if="unitLabels" class="unit-label">{{ hourLabel }}</span>
    </div>
    <div v-if="!unitLabels" class="divider">{{ divider }}</div>
    <div class="unit">
      <span class="value">{{ mins }}</span>
      <span v-if="unitLabels" class="unit-label">{{ minuteLabel }}</span>
    </div>
    <div v-if="!unitLabels" class="divider">{{ divider }}</div>
    <div class="unit">
      <span class="value">{{ secs }}</span>
      <span v-if="unitLabels" class="unit-label">{{ secondLabel }}</span>
    </div>
  </div>
</template>

<script>
import { intervalToDuration } from "date-fns";
import toDate from "date-fns-tz/toDate";
export default {
  name: "Countdown",
  props: {
    end: {
      type: String,
      required: true,
    },
    timeZone: {
      type: String,
      default: "Europe/London",
    },
    theme: {
      type: String,
      default: "utility",
      validator(value) {
        return ["utility", "editorial"].includes(value);
      },
    },
    // Suffix each value with d / h / m / s instead of using ":" dividers
    unitLabels: {
      type: Boolean,
      default: false,
    },
    dayLabel: {
      type: String,
      default: "d",
    },
    hourLabel: {
      type: String,
      default: "h",
    },
    minuteLabel: {
      type: String,
      default: "m",
    },
    secondLabel: {
      type: String,
      default: "s",
    },
  },
  emits: ["ended"],
  data() {
    return {
      now: new Date(),
      tick: null,
      days: null,
      hours: null,
      mins: null,
      secs: null,
      hasEnded: false,
    };
  },
  computed: {
    divider() {
      return this.theme === "editorial" ? " : " : ":";
    },
    endDate() {
      // `end` may carry its own offset (e.g. "2026-03-01T12:00:00+0000" from
      // Liquid); toDate honours that and only falls back to `timeZone` for
      // offset-less strings.
      return toDate(this.end, {
        timeZone: this.timeZone,
      });
    },
    endIsValid() {
      return !Number.isNaN(this.endDate.getTime());
    },
    remaining() {
      // Clamp at zero so the display never runs negative once `end` passes
      // (or if we were handed an unparseable date).
      if (!this.endIsValid || this.now >= this.endDate) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return intervalToDuration({
        start: this.now,
        end: this.endDate,
      });
    },
  },
  mounted() {
    const updateRemaining = () => {
      this.now = new Date();

      if (!this.hasEnded && this.endIsValid && this.now >= this.endDate) {
        this.hasEnded = true;
        this.$emit("ended");
      }

      this.days = this.remaining.days.toLocaleString("en-GB", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
      this.hours = this.remaining.hours.toLocaleString("en-GB", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
      this.mins = this.remaining.minutes.toLocaleString("en-GB", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
      this.secs = this.remaining.seconds.toLocaleString("en-GB", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
    };

    // Populate immediately so there is no initial 1s delay,
    // then keep updating once per second.
    updateRemaining();
    this.tick = setInterval(updateRemaining, 1000);
  },
  beforeUnmount() {
    if (this.tick) {
      clearInterval(this.tick);
      this.tick = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.countdown {
  display: flex;
  color: white;
  align-items: center;
  justify-content: center;
  margin: 0;

  .unit {
    flex: 0 0 auto;
    margin: 0 2px;
  }

  .divider {
    @include utility-body(15px);
    flex: 0 1 auto;
    margin: 0 2px;
  }

  .value {
    @include utility-body(15px);
  }

  .unit-label {
    @include utility-body(15px);
  }

  &--unit-labels {
    .unit + .unit {
      margin-left: 6px;
    }
  }

  &--editorial {
    .unit,
    .divider {
      margin: 0;
    }

    .divider,
    .value {
      @include editorial-headline(24px);
      text-transform: uppercase;
    }

    .divider {
      white-space: pre;
    }
  }

  .label {
    display: block;
    text-align: center;
    text-transform: uppercase;
    font-size: 10px;
    @include respond-above(md) {
      font-size: 20px;
    }
  }
}
</style>
